import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { invitationTokenSchema } from "@ortura/shared";
import { useApi } from "../api/api-context";
import { acceptInvitation, meQuery, queryKeys } from "../api/platform";
import { CenteredPage } from "../components/centered-page";
import { ErrorMessage } from "../components/error-message";

// Einladung annehmen: /invite#<token> (Entscheidung 30). Das Token steht nach
// dem "#": dieser Teil der Adresse wird nie an einen Server geschickt und
// landet so in keinem Zugriffslog. Die Seite liegt hinter der Anmeldung; der
// Rücksprung nach dem Login behält das "#token" bei.

// Wie im Login-Rücksprung: ein Token nur EINMAL einlösen, auch wenn React den
// Effekt in der Entwicklung doppelt ausführt.
const redeemed = new Map<string, Promise<string>>();

export function InvitePage() {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const router = useRouter();
  const [error, setError] = useState<unknown>(null);

  const parsed = invitationTokenSchema.safeParse(window.location.hash.slice(1));
  const token = parsed.success ? parsed.data : null;

  useEffect(() => {
    if (!token) return;
    let promise = redeemed.get(token);
    if (!promise) {
      promise = (async () => {
        const result = await acceptInvitation(api, token);
        // Neue Mitgliedschaft laden und deren Subdomain für die Adresse suchen.
        await queryClient.invalidateQueries({ queryKey: queryKeys.me });
        const me = await queryClient.fetchQuery(meQuery(api));
        const membership = me.memberships.find((m) => m.tenantId === result.tenantId);
        return membership ? `/t/${membership.subdomain}` : "/select";
      })();
      redeemed.set(token, promise);
    }
    let active = true;
    promise.then(
      (target) => {
        // replace: das Token soll nicht in der Browser-Historie bleiben.
        if (active) router.history.replace(target);
      },
      (err: unknown) => {
        if (active) setError(err);
      },
    );
    return () => {
      active = false;
    };
  }, [token, api, queryClient, router]);

  return (
    <CenteredPage>
      <div className="flex flex-col gap-4">
        {!token ? (
          <p role="alert" className="text-sm text-error-fg">
            {t("invite.missingToken")}
          </p>
        ) : error !== null ? (
          <>
            <p className="text-sm text-ink">{t("invite.failed")}</p>
            <ErrorMessage error={error} />
          </>
        ) : (
          <p className="text-sm text-ink-muted">{t("invite.working")}</p>
        )}
        {(!token || error !== null) && (
          <Link to="/select" className="text-sm text-link-fg hover:underline">
            {t("app.backToStart")}
          </Link>
        )}
      </div>
    </CenteredPage>
  );
}
